import {
  removeCleanOrNotDefined,
  useCatalogueRaisonneeSpecificArtworksFormData,
  useCatalogueRaisonneGeneralFormData,
  useCatalogueRaisonneSpecificMutliplesFormData,
  useCreateCatalogueRaisonneGeneral,
  useCreateCatalogueRaisonneSpecifique,
  useSections,
  useUpdateCatalogueRaisonneGeneral,
  useUpdateCatalogueRaisonneSections,
  useUpdateCatalogueRaisonneSpecifique,
  wrapHandleSubmitWithAction,
} from "@/api/cms";
import {
  CatalogueRaisonneGeneralForm,
  CatalogueRaisonneSpecificForm,
} from "@/components/cms/CatalogueRaisonneForm";
import DecoupageCatalogueRaisonneForm from "@/components/cms/DecoupageCatalogueRaisonneForm";
import * as AF from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  constructLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
  supportedLocales,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import {
  baseCmsFormSchema,
  catalogueRaisonneGeneralContent,
  catalogueRaisonneSectionUpsertSchema,
  catalogueRaisonneSpecificContent,
} from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { FormProvider, Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const catalogueRaisonneGeneralSchema = multilingualFieldSchema(
  baseCmsFormSchema.merge(
    catalogueRaisonneGeneralContent.omit({
      id: true,
    }),
  ),
);

const catalogueRaisonneSpecificSchema = multilingualFieldSchema(
  baseCmsFormSchema.merge(
    catalogueRaisonneSpecificContent.omit({
      id: true,
    }),
  ),
);

const formSchema = z.object({
  general: catalogueRaisonneGeneralSchema,
  specificArtworks: catalogueRaisonneSpecificSchema,
  specificMultiples: catalogueRaisonneSpecificSchema,
});

type FormData = z.infer<typeof formSchema>;

const sectionSchema = z.object({
  type: z.string().optional(),
  target: z.enum(["ARTWORKS", "MULTIPRINTS"]).optional(),
  decoupages: z.array(catalogueRaisonneSectionUpsertSchema).optional(),
});

type SectionData = z.infer<typeof sectionSchema>;

const CRCms = () => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();
  const cmsID = instanceData?.CMS.id;

  const { toast } = useToast();

  const { data: formGeneralData } = useCatalogueRaisonneGeneralFormData();
  const updateGeneral = useUpdateCatalogueRaisonneGeneral();
  const createGeneral = useCreateCatalogueRaisonneGeneral();

  const {
    data: formSpecificArtworksData,
    isLoading: isLoadingSpecificArtworks,
    error: errorSpecificArtworks,
  } = useCatalogueRaisonneeSpecificArtworksFormData();
  const {
    data: formSpecificMultiplesData,
    isLoading: isLoadingSpecificMultiples,
    error: errorSpecificMultiples,
  } = useCatalogueRaisonneSpecificMutliplesFormData();

  const {
    data: decoupagesArtworks,
    isLoading: isLoadingDecoupagesArtworks,
    error: errorDecoupagesArtworks,
  } = useSections(cmsID ?? "", "ARTWORKS");
  const {
    data: decoupagesMultiples,
    isLoading: isLoadingDecoupagesMultiples,
    error: errorDecoupagesMultiples,
  } = useSections(cmsID ?? "", "MULTIPRINTS");

  const updateSpecific = useUpdateCatalogueRaisonneSpecifique();
  const createSpecific = useCreateCatalogueRaisonneSpecifique();

  const formGeneralDefaultValues = useMemo(
    () =>
      constructLocaleDict((lang) => {
        const general = formGeneralData?.[lang];
        return {
          ...general?.content,
          published: general?.published ?? false,
          pageName: general?.pageName ?? "",
        };
      }),
    [formGeneralData],
  );

  const formSpecificArtworksDefaultValues: FormData["specificArtworks"] =
    useMemo(
      () =>
        constructLocaleDict((lang) => {
          const specificArtworks = formSpecificArtworksData?.[lang];
          return {
            ...specificArtworks?.content,
            type: "ARTWORKS",
            published: specificArtworks?.published ?? false,
            pageName: specificArtworks?.pageName ?? "",
          };
        }),
      [formSpecificArtworksData],
    );

  const formSpecificMultiplesDefaultValues: FormData["specificMultiples"] =
    useMemo(
      () =>
        constructLocaleDict((lang) => {
          const specificMultiples = formSpecificMultiplesData?.[lang];
          return {
            ...specificMultiples?.content,
            type: "MULTIPRINTS",
            published: specificMultiples?.published ?? false,
            pageName: specificMultiples?.pageName ?? "",
          };
        }),
      [formSpecificMultiplesData],
    );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      general: formGeneralDefaultValues,
      specificArtworks: formSpecificArtworksDefaultValues,
      specificMultiples: formSpecificMultiplesDefaultValues,
    },
  });

  // quite a dirty hack, but we would need to better handle the promise with a suspense
  useEffect(() => {
    form.reset({
      ...form.getValues(),
      general: formGeneralDefaultValues,
    });
  }, [formGeneralDefaultValues, form]);

  useEffect(() => {
    form.reset({
      ...form.getValues(),
      specificArtworks: formSpecificArtworksDefaultValues,
    });
  }, [formSpecificArtworksDefaultValues, form]);

  useEffect(() => {
    form.reset({
      ...form.getValues(),
      specificMultiples: formSpecificMultiplesDefaultValues,
    });
  }, [formSpecificMultiplesDefaultValues, form]);

  const formDecoupageArtworks = useForm<SectionData>({
    resolver: zodResolver(sectionSchema),
    defaultValues: {
      type: undefined,
      target: "ARTWORKS",
      decoupages: [],
    },
  });

  const formDecoupageMultipleprints = useForm<SectionData>({
    resolver: zodResolver(sectionSchema),
    defaultValues: {
      type: undefined,
      target: "MULTIPRINTS",
      decoupages: [],
    },
  });

  /**
   * Restore "type" field values cleared by the clean-up step setting all empty fields to undefined.
   * Since "type" is not user-controlled, it doesn't reappear when the user inputs something in the form,
   * which makes for a validation error on the next submit if not restored.
   */
  const restoreMissingTypeFields = () => {
    supportedLocales.forEach((lang) => {
      const artworksType = form.getValues(
        `specificArtworks.${lang}.type` as Path<FormData>,
      );
      if (artworksType === undefined) {
        form.setValue(
          `specificArtworks.${lang}.type` as Path<FormData>,
          "ARTWORKS",
        );
      }

      const multiplesType = form.getValues(
        `specificMultiples.${lang}.type` as Path<FormData>,
      );
      if (multiplesType === undefined) {
        form.setValue(
          `specificMultiples.${lang}.type` as Path<FormData>,
          "MULTIPRINTS",
        );
      }
    });
  };

  useEffect(() => {
    if (decoupagesArtworks) {
      formDecoupageArtworks.reset({
        type: decoupagesArtworks[0]?.type ?? "creationDate",
        target: "ARTWORKS",
        decoupages: decoupagesArtworks,
      });
    }
  }, [decoupagesArtworks, formDecoupageArtworks]);

  useEffect(() => {
    if (decoupagesMultiples) {
      formDecoupageMultipleprints.reset({
        type: decoupagesMultiples[0]?.type ?? "creationDate",
        target: "MULTIPRINTS",
        decoupages: decoupagesMultiples,
      });
    }
  }, [decoupagesMultiples, formDecoupageMultipleprints]);

  const { mutate: mutateSections } = useUpdateCatalogueRaisonneSections();

  const submitDecoupagesArtworks = async (data: SectionData) => {
    if (
      isLoadingSpecificArtworks ||
      errorSpecificArtworks ||
      isLoadingSpecificMultiples ||
      errorSpecificMultiples ||
      !data.decoupages
    )
      return;

    if (cmsID == null) {
      console.error("Tried to submit but cmsId is null");
      return;
    }

    mutateSections(
      {
        cmsId: cmsID,
        type: data.type ?? "creationDate",
        target: data.target ?? "ARTWORKS",
        decoupages: data.decoupages,
      },
      {
        onSuccess: () => {
          toast({
            title: t("success"),
            description: t("decoupageSuccessMessage"),
            duration: 1000,
            variant: "success",
          });
        },
        onError: (error: any) => {
          console.error("Error updating catalogue raisonné:", error);
          toast({
            title: t("error"),
            description: t("decoupageErrorMessage"),
            variant: "error",
            duration: 1000,
          });
        },
      },
    );
  };

  const submitArtworksDecoupages = async () => {
    if (isLoadingDecoupagesArtworks || errorDecoupagesArtworks) return;
    const data = formDecoupageArtworks.getValues();
    submitDecoupagesArtworks({ ...data, target: "ARTWORKS" });
  };

  const submitMultipleprintsDecoupages = async () => {
    if (isLoadingDecoupagesMultiples || errorDecoupagesMultiples) return;

    const data = formDecoupageMultipleprints.getValues();
    submitDecoupagesArtworks({ ...data, target: "MULTIPRINTS" });
  };

  const onSubmit = (submittedData: FormData) => {
    submitArtworksDecoupages();
    submitMultipleprintsDecoupages();

    Object.keys(submittedData.general).forEach((key) => {
      const localeData = submittedData.general[key as SupportedLocale];
      const baseData = formGeneralData?.[key as SupportedLocale];
      if (
        !localeData ||
        !form.getFieldState(`general.${key}` as Path<FormData>).isDirty
      )
        return;

      const mutation = baseData?.content.id ? updateGeneral : createGeneral;

      const { pageName, published, ...content } = localeData;

      mutation.mutate({
        id: baseData?.content.id ?? "",
        lang: key as SupportedLocale,
        data: {
          pageName,
          published,
          content,
        },
      });
    });

    Object.keys(submittedData.specificArtworks).forEach((key) => {
      const localeData = submittedData.specificArtworks[key as SupportedLocale];
      const baseData = formSpecificArtworksData?.[key as SupportedLocale];
      if (
        !localeData ||
        !form.getFieldState(`specificArtworks.${key}` as Path<FormData>).isDirty
      )
        return;

      const mutation = baseData?.content.id ? updateSpecific : createSpecific;

      const { pageName, published, ...content } = localeData;

      mutation.mutate({
        id: baseData?.content.id ?? "",
        lang: key as SupportedLocale,
        data: {
          pageName,
          published,
          content,
        },
      });
    });

    Object.keys(submittedData.specificMultiples).forEach((key) => {
      const localeData =
        submittedData.specificMultiples[key as SupportedLocale];
      const baseData = formSpecificMultiplesData?.[key as SupportedLocale];
      if (
        !localeData ||
        !form.getFieldState(`specificMultiples.${key}` as Path<FormData>)
          .isDirty
      )
        return;

      const mutation = baseData?.content.id ? updateSpecific : createSpecific;

      const { pageName, published, ...content } = localeData;

      mutation.mutate({
        id: baseData?.content.id ?? "",
        lang: key as SupportedLocale,
        data: {
          pageName,
          published,
          content,
        },
      });
    });

    restoreMissingTypeFields();
  };

  const cleanBeforeSubmit = () => {
    removeCleanOrNotDefined(form, formGeneralData, "general");
    removeCleanOrNotDefined(form, formSpecificArtworksData, "specificArtworks");
    removeCleanOrNotDefined(
      form,
      formSpecificMultiplesData,
      "specificMultiples",
    );
  };

  const tabs = [
    {
      value: "general",
      label: t("cms.catalogueRaisonne.tabs.general"),
    },
    {
      value: "specific-artworks",
      label: t("cms.catalogueRaisonne.tabs.artworks"),
    },
    {
      value: "specific-multiples",
      label: t("cms.catalogueRaisonne.tabs.multiples"),
    },
    {
      value: "structure-artworks",
      label: t("cms.catalogueRaisonne.tabs.structure.artworks"),
    },
    {
      value: "structure-multiples",
      label: t("cms.catalogueRaisonne.tabs.structure.multiples"),
    },
  ] as const;

  return (
    <CMSLayout
      pageName={t("cmsTabCatalogueRaisonne")}
      formIds={["catalogue-raisonne"]}
    >
      <form
        id="catalogue-raisonne"
        style={{ width: "100%" }}
        onSubmit={wrapHandleSubmitWithAction(
          form,
          cleanBeforeSubmit,
          onSubmit,
          (errors) => {
            console.error("Validation errors:", errors);
          },
        )}
      >
        <AF.AbstractForm
          form={form}
          tabs={tabs}
          model="cms"
          classname="pt-0 px-0"
        >
          <AF.Tab value="general">
            <DoubleMultilingualField
              corner={(lang) => (
                <AF.Switch
                  dispLabel={false}
                  name={`general.${lang}.published`}
                />
              )}
              render={(lang) => (
                <CatalogueRaisonneGeneralForm formBase={`general.${lang}`} />
              )}
            />
          </AF.Tab>
          <AF.Tab value="specific-artworks">
            <DoubleMultilingualField
              corner={(lang) => (
                <AF.Switch
                  dispLabel={false}
                  name={`specificArtworks.${lang}.published`}
                />
              )}
              render={(lang) => (
                <CatalogueRaisonneSpecificForm
                  positionBase="CRAccueil"
                  formBase={`specificArtworks.${lang}`}
                />
              )}
            />
          </AF.Tab>
          <AF.Tab value="specific-multiples">
            <DoubleMultilingualField
              corner={(lang) => (
                <AF.Switch
                  dispLabel={false}
                  name={`specificMultiples.${lang}.published`}
                />
              )}
              render={(lang) => (
                <CatalogueRaisonneSpecificForm
                  positionBase="CRAccueilEM"
                  formBase={`specificMultiples.${lang}`}
                />
              )}
            />
          </AF.Tab>
          <AF.Tab value="structure-artworks" isForm={false} showHeader={false}>
            <FormProvider {...formDecoupageArtworks}>
              <DecoupageCatalogueRaisonneForm
                form={formDecoupageArtworks}
                target="ARTWORKS"
                sections={decoupagesArtworks ?? []}
                cmsID={cmsID ?? ""}
              />
            </FormProvider>
          </AF.Tab>
          <AF.Tab value="structure-multiples" isForm={false} showHeader={false}>
            <FormProvider {...formDecoupageMultipleprints}>
              <DecoupageCatalogueRaisonneForm
                form={formDecoupageMultipleprints}
                target="MULTIPRINTS"
                sections={decoupagesMultiples ?? []}
                cmsID={cmsID ?? ""}
              />
            </FormProvider>
          </AF.Tab>
        </AF.AbstractForm>
      </form>
    </CMSLayout>
  );
};

export default CRCms;
